import type { Schema } from '../api/client';
import { Field } from './shared';

export type PlannedShot = Schema<'ProductionShot'>;
type Role = { characterId: string; name: string };
export type ArtDesign = {
  sceneDesigns?: Schema<'SceneDesign'>[];
  propDesigns?: Schema<'PropDesign'>[];
};
const at = (frame: number, fps: number) => `${Number((frame / fps).toFixed(2))}`;
const audience = (line: Schema<'ShotDialogueLine'>) =>
  line.addressMode === 'self'
    ? '自言自语'
    : line.addressMode === 'voiceover'
      ? '画外讲述'
      : `对${line.addressees.map((r) => r.name).join('、')}说`;
export function ArtDirectionEditor({
  design,
  change,
}: {
  design: ArtDesign;
  change: (design: ArtDesign) => void;
}) {
  return (
    <div className="shot-plan-editor">
      <h3>共享场景设计</h3>
      {design.sceneDesigns?.map((scene, i) => (
        <details className="shot-plan-edit-card" key={scene.id}>
          <summary>{scene.name}</summary>
          {(['name', 'period', 'layout', 'lighting', 'details'] as const).map((k, j) => (
            <Field
              key={k}
              label={
                ['场景名称', '年代与地域', '空间布局与家具位置', '光源方向与色调', '环境细节'][j]!
              }
            >
              <textarea
                rows={3}
                value={scene[k]}
                onChange={(e) =>
                  change({
                    ...design,
                    sceneDesigns: design.sceneDesigns!.map((s, n) =>
                      n === i ? { ...s, [k]: e.target.value } : s,
                    ),
                  })
                }
              />
            </Field>
          ))}
        </details>
      ))}
      <button
        type="button"
        className="button small secondary"
        onClick={() =>
          change({
            ...design,
            sceneDesigns: [
              ...(design.sceneDesigns ?? []),
              {
                id: crypto.randomUUID(),
                name: '新场景',
                period: '',
                layout: '',
                lighting: '',
                details: '',
              },
            ],
          })
        }
      >
        添加共享场景
      </button>
      <h3>共享道具设计</h3>
      {design.propDesigns?.map((prop, i) => (
        <details className="shot-plan-edit-card" key={prop.id}>
          <summary>{prop.name}</summary>
          {(['name', 'material', 'scale', 'appearance', 'defaultState', 'purpose'] as const).map(
            (k, j) => (
              <Field
                key={k}
                label={
                  [
                    '道具名称',
                    '材质',
                    '尺寸与人体比例',
                    '形制与外观',
                    '默认状态',
                    '用途与出现理由',
                  ][j]!
                }
              >
                <textarea
                  rows={2}
                  value={prop[k]}
                  onChange={(e) =>
                    change({
                      ...design,
                      propDesigns: design.propDesigns!.map((p, n) =>
                        n === i ? { ...p, [k]: e.target.value } : p,
                      ),
                    })
                  }
                />
              </Field>
            ),
          )}
        </details>
      ))}
      <button
        type="button"
        className="button small secondary"
        onClick={() =>
          change({
            ...design,
            propDesigns: [
              ...(design.propDesigns ?? []),
              {
                id: crypto.randomUUID(),
                name: '新道具',
                material: '',
                scale: '',
                appearance: '',
                defaultState: '',
                purpose: '',
              },
            ],
          })
        }
      >
        添加共享道具
      </button>
    </div>
  );
}
export function ShotPlanView({
  shot,
  fps = 24,
  design = {},
}: {
  shot: PlannedShot;
  fps?: number;
  design?: ArtDesign;
}) {
  if (!shot.staging) return <p>{shot.description}</p>;
  const stage = shot.staging;
  return (
    <div className="shot-plan">
      <p className="shot-plan-scene">
        {stage.scene} · {shot.durationFrames / fps} 秒
      </p>
      <p>{shot.description}</p>
      <dl className="shot-plan-spec">
        {design.sceneDesigns
          ?.filter((s) => s.id === stage.sceneId)
          .map((s) => (
            <div style={{ display: 'contents' }} key={s.id}>
              <dt>场景设计</dt>
              <dd>
                {s.period}。{s.layout}。{s.details}。光源：{s.lighting}。
              </dd>
            </div>
          ))}
        <dt>景别与构图</dt>
        <dd>{stage.framing}</dd>
        <dt>机位</dt>
        <dd>{stage.camera}</dd>
        <dt>光线</dt>
        <dd>{stage.lighting}</dd>
        <dt>开场瞬间</dt>
        <dd>{stage.opening}</dd>
      </dl>
      <h4>人物调度 · 开场 {stage.blocking.filter((r) => r.presentAtStart).length} 人在画内</h4>
      {stage.blocking.map((r) => (
        <p key={r.characterId}>
          <strong>{r.name}</strong> · {r.presentAtStart ? '开场在画内' : '稍后入画'} · {r.position}
          。{r.pose}；{r.expression}；视线：{r.gaze}。
        </p>
      ))}
      {!!stage.props.length && (
        <>
          <h4>道具</h4>
          {stage.props.map((p, i) => {
            const common = design.propDesigns?.find((d) => d.id === p.propId);
            return (
              <p key={i}>
                {common?.name ?? p.name}：
                {common
                  ? `${common.material}；${common.scale}；${common.appearance}`
                  : p.appearance}
                ；{p.position}；{p.state}。
              </p>
            );
          })}
        </>
      )}
      <h4>动作与声音时间轴</h4>
      {shot.actionBeats?.map((b, i) => (
        <div className="shot-plan-beat" key={i}>
          <span>
            {at(b.startFrame, fps)}–{at(b.endFrame, fps)} 秒
          </span>
          <p>
            {b.action}
            {b.sound && <small>环境 / 动作声：{b.sound}</small>}
          </p>
        </div>
      ))}
      <h4>逐句对白</h4>
      {shot.dialogueLines?.length ? (
        shot.dialogueLines.map((l, i) => (
          <div className="shot-plan-beat" key={i}>
            <span>
              {at(l.startFrame, fps)}–{at(l.endFrame, fps)} 秒
            </span>
            <p>
              <strong>
                {l.speaker} → {audience(l)} · {l.mode === 'narration' ? '画外旁白' : '画内对白'}
              </strong>
              （{l.delivery}）<br />
              {l.text}
              <small>
                视线：{l.gaze}；听者反应：{l.listenerReaction || '无'}
              </small>
            </p>
          </div>
        ))
      ) : (
        <p className="quiet">本镜无对白，请核对动作与停顿是否有剧情作用。</p>
      )}
      {!!stage.continuityNotes.length && (
        <p className="quiet">本镜衔接：{stage.continuityNotes.join('；')}</p>
      )}
    </div>
  );
}

export function ShotPlanEditor({
  shot,
  change,
  roles = [],
  fps = 24,
  design = {},
}: {
  shot: PlannedShot;
  change: (shot: PlannedShot) => void;
  roles?: Role[];
  fps?: number;
  design?: ArtDesign;
}) {
  if (!shot.staging || !shot.actionBeats || !shot.dialogueLines) return null;
  const stage = shot.staging;
  const choices = [...roles];
  for (const r of stage.blocking)
    if (!choices.some((c) => c.characterId === r.characterId))
      choices.push({ characterId: r.characterId, name: r.name });
  const timing = (
    item: { startFrame: number; endFrame: number },
    patch: (p: { startFrame?: number; endFrame?: number }) => void,
  ) => (
    <div className="shot-plan-times">
      {(['startFrame', 'endFrame'] as const).map((k) => (
        <Field key={k} label={k === 'startFrame' ? '开始（秒）' : '结束（秒）'}>
          <input
            type="number"
            min={0}
            max={shot.durationFrames / fps}
            step={1 / fps}
            value={at(item[k], fps)}
            onChange={(e) => patch({ [k]: Math.round(Number(e.target.value) * fps) })}
          />
        </Field>
      ))}
    </div>
  );
  return (
    <div className="shot-plan-editor">
      <p className="quiet">逐句对白和动作是生成依据，剧本正文会据此同步整理。时间相对本镜起点。</p>
      <Field label="引用共享场景">
        <select
          value={stage.sceneId}
          onChange={(e) => change({ ...shot, staging: { ...stage, sceneId: e.target.value } })}
        >
          {design.sceneDesigns?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </Field>
      {(['scene', 'framing', 'camera', 'lighting', 'opening'] as const).map((k, i) => (
        <Field
          key={k}
          label={
            [
              '场景 · 内外景 / 地点 / 日夜',
              '景别与构图',
              '机位与视线轴',
              '光线',
              '开场瞬间 · 只描述第0帧',
            ][i]!
          }
        >
          <textarea
            rows={k === 'opening' ? 3 : 2}
            value={stage[k]}
            onChange={(e) => change({ ...shot, staging: { ...stage, [k]: e.target.value } })}
          />
        </Field>
      ))}
      <h4>逐人调度</h4>
      {stage.blocking.map((r, i) => (
        <div className="shot-plan-edit-card" key={r.characterId}>
          <strong>{r.name}</strong>
          <label className="novel-check">
            <input
              type="checkbox"
              checked={r.presentAtStart}
              onChange={(e) =>
                change({
                  ...shot,
                  staging: {
                    ...stage,
                    blocking: stage.blocking.map((v, j) =>
                      j === i ? { ...v, presentAtStart: e.target.checked } : v,
                    ),
                  },
                })
              }
            />
            开场在画内
          </label>
          {(['position', 'pose', 'expression', 'gaze'] as const).map((k, j) => (
            <Field key={k} label={['站位与前后景', '起始姿态与持物', '表情', '视线对象'][j]!}>
              <textarea
                rows={2}
                value={r[k]}
                onChange={(e) =>
                  change({
                    ...shot,
                    staging: {
                      ...stage,
                      blocking: stage.blocking.map((v, n) =>
                        n === i ? { ...v, [k]: e.target.value } : v,
                      ),
                    },
                  })
                }
              />
            </Field>
          ))}
          <button
            type="button"
            className="button small secondary"
            onClick={() =>
              change({
                ...shot,
                characterIds: (shot.characterIds ?? []).filter((id) => id !== r.characterId),
                staging: { ...stage, blocking: stage.blocking.filter((_, j) => j !== i) },
                dialogueLines: shot.dialogueLines!.filter((l) => l.characterId !== r.characterId),
              })
            }
          >
            移出本镜（同时移除其台词）
          </button>
        </div>
      ))}
      <select
        aria-label="添加出场角色"
        value=""
        onChange={(e) => {
          const r = choices.find((c) => c.characterId === e.target.value);
          if (r)
            change({
              ...shot,
              characterIds: [...(shot.characterIds ?? []), r.characterId],
              staging: {
                ...stage,
                blocking: [
                  ...stage.blocking,
                  { ...r, presentAtStart: true, position: '', pose: '', expression: '', gaze: '' },
                ],
              },
            });
        }}
      >
        <option value="">添加出场角色…</option>
        {choices
          .filter((r) => !stage.blocking.some((b) => b.characterId === r.characterId))
          .map((r) => (
            <option key={r.characterId} value={r.characterId}>
              {r.name}
            </option>
          ))}
      </select>
      <h4>本镜道具</h4>
      {stage.props.map((p, i) => (
        <div className="shot-plan-edit-card" key={i}>
          <strong>
            {design.propDesigns?.find((d) => d.id === p.propId)?.name ?? p.name ?? p.propId}
          </strong>
          {(['position', 'state'] as const).map((k, j) => (
            <Field key={k} label={['起始位置 / 持有人', '本镜起始状态'][j]!}>
              <textarea
                rows={2}
                value={p[k]}
                onChange={(e) =>
                  change({
                    ...shot,
                    staging: {
                      ...stage,
                      props: stage.props.map((v, n) =>
                        n === i ? { ...v, [k]: e.target.value } : v,
                      ),
                    },
                  })
                }
              />
            </Field>
          ))}
          <button
            type="button"
            className="button small secondary"
            onClick={() =>
              change({
                ...shot,
                staging: { ...stage, props: stage.props.filter((_, n) => n !== i) },
              })
            }
          >
            移出本镜
          </button>
        </div>
      ))}
      <select
        aria-label="添加本镜道具"
        value=""
        onChange={(e) => {
          const p = design.propDesigns?.find((d) => d.id === e.target.value);
          if (p)
            change({
              ...shot,
              staging: {
                ...stage,
                props: [
                  ...stage.props,
                  {
                    propId: p.id,
                    name: p.name,
                    appearance: p.appearance,
                    position: '',
                    state: p.defaultState,
                  },
                ],
              },
            });
        }}
      >
        <option value="">从共享设计添加道具…</option>
        {design.propDesigns
          ?.filter((d) => !stage.props.some((p) => p.propId === d.id))
          .map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
      </select>
      <h4>动作时间轴</h4>
      {shot.actionBeats.map((b, i) => (
        <div className="shot-plan-edit-card" key={i}>
          {timing(b, (p) =>
            change({
              ...shot,
              actionBeats: shot.actionBeats!.map((v, j) => (j === i ? { ...v, ...p } : v)),
            }),
          )}
          <Field label="具体动作与反应">
            <textarea
              rows={3}
              value={b.action}
              onChange={(e) =>
                change({
                  ...shot,
                  actionBeats: shot.actionBeats!.map((v, j) =>
                    j === i ? { ...v, action: e.target.value } : v,
                  ),
                })
              }
            />
          </Field>
          <Field label="环境 / 动作声">
            <input
              value={b.sound}
              onChange={(e) =>
                change({
                  ...shot,
                  actionBeats: shot.actionBeats!.map((v, j) =>
                    j === i ? { ...v, sound: e.target.value } : v,
                  ),
                })
              }
            />
          </Field>
          <button
            type="button"
            className="button small secondary"
            onClick={() =>
              change({ ...shot, actionBeats: shot.actionBeats!.filter((_, j) => j !== i) })
            }
          >
            删除此动作段
          </button>
        </div>
      ))}
      <button
        type="button"
        className="button small secondary"
        onClick={() =>
          change({
            ...shot,
            actionBeats: [
              ...shot.actionBeats!,
              {
                startFrame: shot.actionBeats!.at(-1)?.endFrame ?? 0,
                endFrame: shot.durationFrames,
                action: '',
                sound: '',
              },
            ],
          })
        }
      >
        增加动作段
      </button>
      <h4>逐句对白</h4>
      {shot.dialogueLines.map((l, i) => (
        <div className="shot-plan-edit-card" key={i}>
          {timing(l, (p) =>
            change({
              ...shot,
              dialogueLines: shot.dialogueLines!.map((v, j) => (j === i ? { ...v, ...p } : v)),
            }),
          )}
          <Field label="说话角色">
            <select
              value={l.characterId}
              onChange={(e) => {
                const role = choices.find((r) => r.characterId === e.target.value);
                change({
                  ...shot,
                  dialogueLines: shot.dialogueLines!.map((v, j) =>
                    j === i
                      ? {
                          ...v,
                          characterId: e.target.value,
                          speaker: role?.name ?? '旁白',
                          mode: role ? v.mode : 'narration',
                          addressMode: role ? v.addressMode : 'voiceover',
                          addressees: role
                            ? v.addressees.filter((a) => a.characterId !== role.characterId)
                            : [],
                        }
                      : v,
                  ),
                });
              }}
            >
              <option value="">画外讲述者</option>
              {choices.map((r) => (
                <option key={r.characterId} value={r.characterId}>
                  {r.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="说给谁听">
            <select
              value={l.addressMode}
              onChange={(e) =>
                change({
                  ...shot,
                  dialogueLines: shot.dialogueLines!.map((v, j) =>
                    j === i
                      ? {
                          ...v,
                          addressMode: e.target.value as 'character' | 'self' | 'voiceover',
                          mode: e.target.value === 'voiceover' ? 'narration' : 'dialogue',
                          addressees: [],
                        }
                      : v,
                  ),
                })
              }
            >
              <option value="character">对角色说话</option>
              <option value="self">自言自语</option>
              <option value="voiceover">画外旁白 / 内心独白</option>
            </select>
          </Field>
          {l.addressMode === 'character' && (
            <Field label="听话人（可选多位）">
              <div>
                {choices
                  .filter((r) => r.characterId !== l.characterId)
                  .map((r) => (
                    <label className="novel-check" key={r.characterId}>
                      <input
                        type="checkbox"
                        checked={l.addressees.some((a) => a.characterId === r.characterId)}
                        onChange={(e) =>
                          change({
                            ...shot,
                            dialogueLines: shot.dialogueLines!.map((v, j) =>
                              j === i
                                ? {
                                    ...v,
                                    addressees: e.target.checked
                                      ? [...v.addressees, r]
                                      : v.addressees.filter((a) => a.characterId !== r.characterId),
                                  }
                                : v,
                            ),
                          })
                        }
                      />
                      {r.name}
                    </label>
                  ))}
              </div>
            </Field>
          )}
          <Field label="说话时的视线方向">
            <input
              value={l.gaze}
              onChange={(e) =>
                change({
                  ...shot,
                  dialogueLines: shot.dialogueLines!.map((v, j) =>
                    j === i ? { ...v, gaze: e.target.value } : v,
                  ),
                })
              }
            />
          </Field>
          <Field label="听话人的反应">
            <input
              value={l.listenerReaction}
              onChange={(e) =>
                change({
                  ...shot,
                  dialogueLines: shot.dialogueLines!.map((v, j) =>
                    j === i ? { ...v, listenerReaction: e.target.value } : v,
                  ),
                })
              }
            />
          </Field>
          <Field label="逐字台词">
            <textarea
              rows={2}
              value={l.text}
              onChange={(e) =>
                change({
                  ...shot,
                  dialogueLines: shot.dialogueLines!.map((v, j) =>
                    j === i ? { ...v, text: e.target.value } : v,
                  ),
                })
              }
            />
          </Field>
          <Field label="此句语气与节奏">
            <input
              value={l.delivery}
              onChange={(e) =>
                change({
                  ...shot,
                  dialogueLines: shot.dialogueLines!.map((v, j) =>
                    j === i ? { ...v, delivery: e.target.value } : v,
                  ),
                })
              }
            />
          </Field>
          <button
            type="button"
            className="button small secondary"
            onClick={() =>
              change({ ...shot, dialogueLines: shot.dialogueLines!.filter((_, j) => j !== i) })
            }
          >
            删除此句
          </button>
        </div>
      ))}
      <button
        type="button"
        className="button small secondary"
        onClick={() =>
          change({
            ...shot,
            dialogueLines: [
              ...shot.dialogueLines!,
              {
                characterId: stage.blocking[0]?.characterId ?? '',
                speaker: stage.blocking[0]?.name ?? '旁白',
                mode: stage.blocking.length ? 'dialogue' : 'narration',
                addressMode: stage.blocking.length ? 'character' : 'voiceover',
                addressees: [],
                gaze: '',
                listenerReaction: '',
                startFrame: shot.dialogueLines!.at(-1)?.endFrame ?? 0,
                endFrame: shot.durationFrames,
                text: '',
                delivery: '',
              },
            ],
          })
        }
      >
        增加一句对白
      </button>
      <Field label="本镜连续性（每行一条）">
        <textarea
          rows={3}
          value={stage.continuityNotes.join('\n')}
          onChange={(e) =>
            change({
              ...shot,
              staging: {
                ...stage,
                continuityNotes: e.target.value.split('\n').filter((s) => s.trim()),
              },
            })
          }
        />
      </Field>
    </div>
  );
}
